/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, timer } from 'rxjs';
import { map } from 'rxjs/operators';

/** Local Imports */
import { AuthService } from '@core/services/auth/auth.service';

const MIN_DISPLAY_MS = 3000;

/**
 * Component displayed while verifying the user's authentication session.
 *
 * @remarks
 * Acts as a landing page during initial load or SSR to avoid flashing
 * the login page or protected dashboard content before the auth state is confirmed.
 */
@Component({
    selector: 'app-check-session',
    imports: [],
    templateUrl: './check-session.component.html',
    styleUrls: ['./check-session.component.scss'],
})
export class CheckSessionComponent implements OnInit {
    /**
     * Authentication service instance
     * @type {AuthService}
     */
    private readonly authService: AuthService = inject(AuthService);

    /**
     * Router service for navigation
     * @type {Router}
     */
    private readonly router: Router = inject(Router);

    /**
     * Activated route service to access query parameters
     * @type {ActivatedRoute}
     */
    private readonly route: ActivatedRoute = inject(ActivatedRoute);

    /**
     * Platform ID to check if running in browser
     * @type {Object}
     */
    private readonly platformId = inject(PLATFORM_ID);

    ngOnInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            forkJoin([this.authService.verifyAuthWithBackend(), timer(MIN_DISPLAY_MS)])
                .pipe(map(([isAuth]) => isAuth))
                .subscribe({
                    next: (isAuth: boolean) => {
                        const rawReturnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
                        const isSafe =
                            typeof rawReturnUrl === 'string' &&
                            /^\/(?![\/\\])[A-Za-z0-9\-._~!$&'()*+,;=:@%\/?#]*$/.test(rawReturnUrl);
                        if (isAuth) {
                            if (isSafe) {
                                this.router.navigate([rawReturnUrl]);
                            } else {
                                this.router.navigate(['/']);
                            }
                        } else {
                            const queryParams =
                                isSafe && rawReturnUrl !== '/' ? { returnUrl: rawReturnUrl } : {};
                            this.router.navigate(['/login'], { queryParams });
                        }
                    },
                    error: () => {
                        this.router.navigate(['/login']);
                    },
                });
        }
    }
}
